import { Layout } from '../components/Layout';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const SPONGE_SIZES = ['4"', '6"', '8"', '10"', '12"', '14"'];

export default function Production({ user, onLogout }) {
  const [showForm, setShowForm] = useState(false);
  const [productionType, setProductionType] = useState('sponge');
  const [products, setProducts] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [formData, setFormData] = useState({
    product_id: '',
    recipe_id: '',
    planned_yield: '',
    actual_yield: '',
    damaged: '0',
    rejected: '0',
    sponge_base: 'chocolate',
  });

  useEffect(() => {
    fetchProducts();
    fetchBatches();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, getAuthHeader());
      setProducts(res.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await axios.get(`${API}/production/batches?date=${today}`, getAuthHeader());
      setBatches(res.data);
    } catch (error) {
      console.error('Failed to fetch batches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const netUsable = parseFloat(formData.actual_yield) - parseFloat(formData.damaged) - parseFloat(formData.rejected);
    
    const batchData = {
      batch_id: `batch_${Date.now()}`,
      date: new Date().toISOString(),
      type: productionType,
      product_id: formData.product_id,
      recipe_id: formData.recipe_id || null,
      planned_yield: parseFloat(formData.planned_yield),
      actual_yield: parseFloat(formData.actual_yield),
      damaged: parseFloat(formData.damaged),
      rejected: parseFloat(formData.rejected),
      net_usable: netUsable,
      unit: 'pcs',
      location_id: 'central_kitchen',
      created_by: user.phone,
    };

    try {
      await axios.post(`${API}/production/batch`, batchData, getAuthHeader());
      toast.success('Production batch recorded successfully');
      setShowForm(false);
      resetForm();
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record production');
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      recipe_id: '',
      planned_yield: '',
      actual_yield: '',
      damaged: '0',
      rejected: '0',
      sponge_base: 'chocolate',
    });
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-stone-900">Production Entry</h1>
            <p className="text-stone-600 mt-1">Record daily production batches</p>
          </div>
          <Button
            data-testid="add-production-button"
            onClick={() => setShowForm(!showForm)}
            className="bg-secondary hover:bg-secondary-hover text-white"
          >
            <Plus size={20} className="mr-2" />
            New Batch
          </Button>
        </div>

        {showForm && (
          <div className="bg-white border border-stone-200 rounded-sm shadow-sm p-6 mb-8">
            <h2 className="font-heading text-2xl font-semibold text-stone-800 mb-6">New Production Batch</h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Production Type</label>
                  <Select value={productionType} onValueChange={setProductionType}>
                    <SelectTrigger data-testid="production-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sponge">Sponge</SelectItem>
                      <SelectItem value="buttercream">Buttercream</SelectItem>
                      <SelectItem value="filling">Filling</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Product</label>
                  <Select value={formData.product_id} onValueChange={(value) => setFormData({ ...formData, product_id: value })}>
                    <SelectTrigger data-testid="product-select">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent>
                      {products.filter(p => p.category === productionType).map(p => (
                        <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Planned Yield</label>
                  <input
                    type="number"
                    data-testid="planned-yield-input"
                    value={formData.planned_yield}
                    onChange={(e) => setFormData({ ...formData, planned_yield: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Actual Yield</label>
                  <input
                    type="number"
                    data-testid="actual-yield-input"
                    value={formData.actual_yield}
                    onChange={(e) => setFormData({ ...formData, actual_yield: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Damaged Quantity</label>
                  <input
                    type="number"
                    data-testid="damaged-input"
                    value={formData.damaged}
                    onChange={(e) => setFormData({ ...formData, damaged: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Rejected Quantity</label>
                  <input
                    type="number"
                    data-testid="rejected-input"
                    value={formData.rejected}
                    onChange={(e) => setFormData({ ...formData, rejected: e.target.value })}
                    className="w-full h-10 px-3 border border-stone-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>

              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-production-button" className="bg-primary hover:bg-primary-hover text-white">
                  Record Production
                </Button>
                <Button type="button" data-testid="cancel-button" onClick={() => setShowForm(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white border border-stone-200 rounded-sm shadow-sm">
          <div className="p-6 border-b border-stone-200">
            <h2 className="font-heading text-2xl font-semibold text-stone-800">Today's Production</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Batch ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Planned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Actual</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Damaged</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-stone-500 uppercase">Net Usable</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((batch) => (
                  <tr key={batch.batch_id} className="border-b border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono text-sm">{batch.batch_id}</td>
                    <td className="px-4 py-3 capitalize">{batch.type}</td>
                    <td className="px-4 py-3 font-mono">{batch.planned_yield}</td>
                    <td className="px-4 py-3 font-mono">{batch.actual_yield}</td>
                    <td className="px-4 py-3 font-mono text-functional-error">{batch.damaged + batch.rejected}</td>
                    <td className="px-4 py-3 font-mono font-semibold text-functional-success">{batch.net_usable}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {batches.length === 0 && (
              <div className="text-center py-12 text-stone-400">No production batches recorded today</div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
